/* Xiaomi 2026 · Approvals Inbox
 * High-value gift approvals queue. Approver clicks Approve / Reject;
 * advances the journey past the approval gate (step 5 → 6) or blocks it.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TABS = [
  { id: 'pending', label: 'Pending', color: '#f59e0b' },
  { id: 'approved', label: 'Approved', color: '#16a34a' },
  { id: 'rejected', label: 'Rejected', color: '#dc2626' },
  { id: 'all', label: 'All', color: '#15110d' },
];


export default function XiaomiApprovals() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [list, sum] = await Promise.all([
        axios.get(`${API}/xiaomi/approvals?status=${tab}`, authHdr()),
        axios.get(`${API}/xiaomi/approvals/queue-summary`, authHdr()),
      ]);
      setRows(list.data.rows || []);
      setSummary(sum.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => { load(); }, [tab]);

  const decide = async (instance_id, decision) => {
    const note = decision === 'reject'
      ? window.prompt('Reason for rejection (will be logged):') || ''
      : '';
    setBusyId(instance_id);
    try {
      await axios.post(`${API}/xiaomi/approvals/${instance_id}/decide`,
        { decision, note }, authHdr());
      toast.success(`Marked ${decision}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="xiaomi-approvals-page">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            Approvals <span style={{ color: '#ff6900' }}>inbox</span>
          </h1>
          <p className="text-sm text-soft mt-1">High-value gifts that need explicit sign-off before dispatch.</p>
        </div>
        <div className="flex gap-1.5">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition"
              style={{
                borderColor: tab === t.id ? t.color : '#e6dccd',
                background:  tab === t.id ? t.color : '#fff',
                color:       tab === t.id ? '#fff' : t.color,
              }}
              data-testid={`approvals-tab-${t.id}`}>
              {t.label} {t.id !== 'all' && summary[t.id] != null && `· ${summary[t.id]}`}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: '#f3ebd9' }}>
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Partner</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Benefit</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Tier</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Cost</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-soft">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center italic text-soft p-8">
                <AlertCircle size={24} className="inline mb-2 opacity-50" />
                <div>No approvals in this queue.</div>
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.instance_id} className="border-t border-line hover:bg-[#fffbf0]">
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => navigate(`/xiaomi/partners/${r.dealer_id}`)}
                    className="font-semibold text-[#15110d] hover:underline text-left"
                    data-testid={`approval-partner-${r.instance_id}`}
                  >
                    {r.outlet_name}
                  </button>
                  <div className="text-[11px] text-soft">{r.owner_name} · {r.city}, {r.state}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div>{r.benefit_name}</div>
                  <div className="text-[11px] text-soft">{r.month_ym} · step {r.current_step}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">{r.tier}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {r.cost_estimate ? `₹ ${r.cost_estimate.toLocaleString('en-IN')}` : '—'}
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{
                      background: r.approval_status === 'approved' ? '#dcfce7' :
                                  r.approval_status === 'rejected' ? '#fee2e2' : '#fef3c7',
                      color:      r.approval_status === 'approved' ? '#166534' :
                                  r.approval_status === 'rejected' ? '#991b1b' : '#92400e',
                    }}>
                    {r.approval_status}
                  </span>
                  {r.approver && (
                    <div className="text-[10px] text-soft mt-1">by {r.approver}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {r.approval_status === 'pending' && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => decide(r.instance_id, 'approve')}
                        disabled={busyId === r.instance_id}
                        className="px-2.5 py-1 rounded text-[11px] font-semibold text-white flex items-center gap-1 disabled:opacity-50"
                        style={{ background: '#16a34a' }}
                        data-testid={`approval-approve-${r.instance_id}`}>
                        <Check size={11} /> Approve
                      </button>
                      <button onClick={() => decide(r.instance_id, 'reject')}
                        disabled={busyId === r.instance_id}
                        className="px-2.5 py-1 rounded text-[11px] font-semibold text-white flex items-center gap-1 disabled:opacity-50"
                        style={{ background: '#dc2626' }}
                        data-testid={`approval-reject-${r.instance_id}`}>
                        <X size={11} /> Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
